import API from '@Services/apiAxios';
import axios from 'axios';

export interface PresignedUrlResponse {
  url: string;
  fields: Record<string, string>;
  key: string;
}

export interface AccountAiFields {
  email?: string;
  name?: string;
  companyName?: string;
  contactNumber?: string;
  address?: string;
  [key: string]: any;
}

export interface AccountExtractionResponse {
  jobId?: string;
  status?: 'pending' | 'processing' | 'completed' | 'needs_review' | 'failed' | string;
  documentType?: string;
  fields?: AccountAiFields;
  confidence?: number;
  invalidFields?: string[];
  error?: string | null;
  [key: string]: any;
}

export interface ExtractionProcessOptions {
  onStatusChange?: (status: string) => void;
  onUploadProgress?: (percent: number) => void;
  isCancelled?: () => boolean;
  registerTimer?: (timer: NodeJS.Timeout | null) => void;
  pollIntervalMs?: number;
}

/**
 * Safely unwrap extraction response object whether returned at root level
 * or wrapped inside response.data.data
 */
export const unwrapExtractionResponse = (raw: any): AccountExtractionResponse => {
  if (!raw) return {};
  if (raw.jobId || raw.status || raw.fields) {
    return raw;
  }
  if (raw.data && typeof raw.data === 'object') {
    if (raw.data.jobId || raw.data.status || raw.data.fields) {
      return {
        ...raw.data,
        error: raw.data.error || raw.error || null,
      };
    }
  }
  return raw;
};

/**
 * Step 1: Get presigned upload URL for extraction
 * POST /speedboxbot/extract/upload-url
 */
export const getUploadUrl = async (file: File): Promise<PresignedUrlResponse> => {
  const { data } = await API.post<PresignedUrlResponse>('/speedboxbot/extract/upload-url', {
    fileName: file.name,
    mimeType: file.type || 'application/octet-stream',
  });
  return data;
};

/**
 * Step 2: Upload file directly to S3 bucket via presigned POST
 */
export const uploadToS3 = async (
  uploadUrlResponse: PresignedUrlResponse,
  file: File,
  onProgress?: (percent: number) => void,
): Promise<void> => {
  const { url, fields } = uploadUrlResponse;
  const formData = new FormData();
  Object.entries(fields).forEach(([key, value]) => {
    formData.append(key, value);
  });
  formData.append('file', file);

  // Use clean axios instance for S3 request to prevent sending custom auth headers to AWS
  await axios.post(url, formData, {
    onUploadProgress: progressEvent => {
      if (onProgress) {
        const percent = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 1));
        onProgress(percent);
      }
    },
  });
};

/**
 * Step 3: Trigger document extraction with documentType = "account_creation" and mode = "async"
 * POST /speedboxbot/extract
 */
export const extractAccountDocument = async (s3Key: string): Promise<AccountExtractionResponse> => {
  const { data } = await API.post<AccountExtractionResponse>('/speedboxbot/extract', {
    documentType: 'account_creation',
    s3Key,
    mode: 'async',
  });
  return unwrapExtractionResponse(data);
};

/**
 * Step 4: Poll extraction job status
 * GET /speedboxbot/extract/{jobId}
 */
export const getAccountExtractionStatus = async (jobId: string): Promise<AccountExtractionResponse> => {
  const { data } = await API.get<AccountExtractionResponse>(`/speedboxbot/extract/${jobId}`);
  return unwrapExtractionResponse(data);
};

/**
 * Full AI extraction runner with async polling:
 * 1. Generate presigned URL
 * 2. Upload to S3
 * 3. Initial async extraction request (mode: "async")
 * 4. Poll every 10 seconds until terminal status (completed, needs_review, failed)
 */
export const processAccountDocumentExtraction = async (
  file: File,
  optionsOrStatusChange?: ((status: string) => void) | ExtractionProcessOptions,
  legacyOnUploadProgress?: (percent: number) => void,
): Promise<AccountExtractionResponse> => {
  let options: ExtractionProcessOptions = {};
  if (typeof optionsOrStatusChange === 'function') {
    options = {
      onStatusChange: optionsOrStatusChange,
      onUploadProgress: legacyOnUploadProgress,
    };
  } else if (optionsOrStatusChange) {
    options = optionsOrStatusChange;
  }

  const { onStatusChange, onUploadProgress, isCancelled, registerTimer, pollIntervalMs = 10000 } = options;

  if (isCancelled?.()) throw new Error('Extraction cancelled');

  // Step 1: Upload URL
  onStatusChange?.('Generating secure upload URL...');
  const presigned = await getUploadUrl(file);

  if (isCancelled?.()) throw new Error('Extraction cancelled');

  // Step 2: Upload to S3
  onStatusChange?.('Uploading document securely to S3...');
  const s3Key = presigned.key || presigned.fields?.['key'];
  await uploadToS3(presigned, file, onUploadProgress);

  if (isCancelled?.()) throw new Error('Extraction cancelled');

  // Step 3: Initial async extraction request
  onStatusChange?.('Submitting document for AI extraction...');
  const initialResponse = await extractAccountDocument(s3Key);

  // If already terminal (e.g. sync fallback response)
  const initialStatus = initialResponse.status;
  if (initialStatus === 'completed' || initialStatus === 'failed' || initialStatus === 'needs_review') {
    return initialResponse;
  }

  const jobId = initialResponse.jobId;
  if (!jobId) {
    if (initialResponse.fields && Object.keys(initialResponse.fields).length > 0) {
      return initialResponse;
    }
    return {
      status: 'failed',
      error: initialResponse.error || 'Failed to initialize extraction: missing jobId in response',
    };
  }

  // Step 4: Polling loop every 10 seconds
  let currentStatus = initialStatus || 'pending';

  while (currentStatus === 'pending' || currentStatus === 'processing') {
    if (isCancelled?.()) throw new Error('Extraction cancelled');

    onStatusChange?.(
      currentStatus === 'processing'
        ? 'AI is analyzing the document... (processing)'
        : 'AI extraction in progress... (waiting for results)',
    );

    // Wait 10 seconds before the poll without overlapping requests
    await new Promise<void>((resolve, reject) => {
      const timer = setTimeout(() => {
        registerTimer?.(null);
        if (isCancelled?.()) {
          reject(new Error('Extraction cancelled'));
        } else {
          resolve();
        }
      }, pollIntervalMs);
      registerTimer?.(timer);
    });

    if (isCancelled?.()) throw new Error('Extraction cancelled');

    onStatusChange?.('Checking extraction status...');
    const pollResult = await getAccountExtractionStatus(jobId);

    currentStatus = pollResult.status || 'failed';

    if (currentStatus === 'completed' || currentStatus === 'failed' || currentStatus === 'needs_review') {
      return pollResult;
    }
  }

  return {
    status: currentStatus,
    error: `Unexpected extraction status: ${currentStatus}`,
  };
};
